import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { LandingHeader } from "./-landing-v1/LandingHeader";
import { LandingFooter } from "./-landing-v1/LandingFooter";

export const Route = createFileRoute("/dev/prototypes/clearvoid-v1")({
	component: FindingsV1,
});

const FONT_HREF =
	"https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Inter:wght@200;300;400&display=swap";

const ICONS_HREF =
	"https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap";

// --- Sample Data ---

const CONTENT = {
	title: "Naval Ravikant on Wealth Creation",
	sourceType: "YouTube",
	source: "The Knowledge Project",
	duration: "1h 22m",
	description:
		'The famous "How to Get Rich" interview with timeless principles on leverage, equity, and specific knowledge.',
	thumbnailUrl:
		"https://lh3.googleusercontent.com/aida-public/AB6AXuD_5Yl-mK9H04PrBMW03rgLt0GAOXYoAjA3s9SKi2Ipb75HuxGIfRw3T7P3uWVvVgvfFQRdOKZ3F-sgv9G-l8jgPHSekKgRjmmUDgsaPX-zSidBibLdropDALN9aT-HD5fLSjgLoU3tU-qv_r3UtXxuqna5hWcllU6nqn3nqKSzgt7Uz0LreE8Wp_4buQDHfRaw7JgksjsIdKy-gQT0-p_dR5P60LAgcec2y2YCOigCAyaFt2C8CVclWChz5UaLe4LnHvMFvY_MyBE",
	speakers: [
		{ name: "Naval Ravikant", role: "Guest" },
		{ name: "Joe Rogan", role: "Host" },
	],
};

type FindingType = "quote" | "claim" | "prediction" | "opinion" | "takeaway";

interface Finding {
	id: string;
	type: FindingType;
	content: string;
	speaker: string;
	timestamp: string;
}

interface Topic {
	name: string;
	findings: Finding[];
}

const TOPICS: Topic[] = [
	{
		name: "Wealth & Status",
		findings: [
			{
				id: "f1",
				type: "quote",
				content:
					"\u201CWealth is assets that earn while you sleep. Money is how we transfer time and wealth. Status is your place in the social hierarchy.\u201D",
				speaker: "Naval Ravikant",
				timestamp: "12:45",
			},
			{
				id: "f2",
				type: "claim",
				content:
					"Status is a zero-sum game. For someone to gain status, someone else has to lose it. Wealth is not zero-sum \u2014 you can create new wealth by building things society wants.",
				speaker: "Naval Ravikant",
				timestamp: "14:20",
			},
			{
				id: "f3",
				type: "claim",
				content:
					"People who are attacking wealth creation are almost always people who are playing status games. They\u2019re trying to bring others down to elevate themselves.",
				speaker: "Naval Ravikant",
				timestamp: "16:03",
			},
		],
	},
	{
		name: "Leverage & Technology",
		findings: [
			{
				id: "f4",
				type: "prediction",
				content:
					"Code and media are permissionless leverage. They\u2019re the leverage of the new rich. In the future, everyone will have an army of robots working for them.",
				speaker: "Naval Ravikant",
				timestamp: "28:10",
			},
			{
				id: "f5",
				type: "claim",
				content:
					"The most powerful form of leverage is products that have no marginal cost of replication \u2014 code, media, podcasts, movies, books. You create it once and it works for you forever.",
				speaker: "Naval Ravikant",
				timestamp: "30:45",
			},
			{
				id: "f6",
				type: "quote",
				content:
					"\u201CIf you can\u2019t code, write books and blogs, record videos and podcasts.\u201D",
				speaker: "Naval Ravikant",
				timestamp: "32:12",
			},
			{
				id: "f7",
				type: "opinion",
				content:
					"That\u2019s the real revolution right now. The barriers to creating media are basically gone. You and a microphone can reach millions of people.",
				speaker: "Joe Rogan",
				timestamp: "33:40",
			},
		],
	},
	{
		name: "Specific Knowledge",
		findings: [
			{
				id: "f8",
				type: "quote",
				content:
					"\u201CSpecific knowledge is found by pursuing your genuine curiosity and passion rather than whatever is hot right now.\u201D",
				speaker: "Naval Ravikant",
				timestamp: "42:15",
			},
			{
				id: "f9",
				type: "takeaway",
				content:
					"Specific knowledge cannot be trained for. If society can train you for it, then it can train someone else and replace you. It\u2019s built through apprenticeship, not schooling.",
				speaker: "Naval Ravikant",
				timestamp: "44:30",
			},
			{
				id: "f10",
				type: "opinion",
				content:
					"The internet has massively broadened the possible space of careers. Most people haven\u2019t figured out that they can find their niche and build an audience of like-minded people.",
				speaker: "Naval Ravikant",
				timestamp: "48:05",
			},
		],
	},
];

const TOTAL_FINDINGS = TOPICS.reduce((n, t) => n + t.findings.length, 0);

// --- Finding Card Config ---

const FINDING_CONFIG: Record<FindingType, { icon: string; label: string }> = {
	quote: { icon: "auto_awesome", label: "Quote" },
	claim: { icon: "lightbulb", label: "Claim" },
	prediction: { icon: "trending_up", label: "Prediction" },
	opinion: { icon: "chat_bubble", label: "Opinion" },
	takeaway: { icon: "bookmark", label: "Takeaway" },
};

const FILTER_OPTIONS: Array<{ key: FindingType | "all"; label: string }> = [
	{ key: "all", label: "All" },
	{ key: "claim", label: "Claims" },
	{ key: "quote", label: "Quotes" },
	{ key: "opinion", label: "Opinions" },
	{ key: "prediction", label: "Predictions" },
];

const TYPE_TEXT: Record<FindingType, string> = {
	quote: "text-quote",
	claim: "text-claim",
	prediction: "text-prediction",
	opinion: "text-opinion",
	takeaway: "text-claim",
};

// --- Components ---

function FindingCard({ finding }: { finding: Finding }) {
	const config = FINDING_CONFIG[finding.type];
	const color = TYPE_TEXT[finding.type];
	const isQuote = finding.type === "quote";

	return (
		<article className="border-ivory/8 group bg-dark-accent hover:border-ivory/15 relative mt-3 rounded-lg border p-7 transition-colors duration-300">
			<span
				className={`${color} bg-dark-accent group-hover:border-ivory/20 border-t-ivory/15 absolute -top-3 left-5 rounded-sm border-t px-3 py-1 text-[10px] font-medium tracking-widest uppercase transition-colors duration-300 select-none`}
			>
				{config.label}
			</span>
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					{finding.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					{finding.content}
				</p>
			)}
		</article>
	);
}

// --- Page ---

function FindingsV1() {
	const [activeFilter, setActiveFilter] = useState<FindingType | "all">(
		"all",
	);

	useEffect(() => {
		const fonts = document.createElement("link");
		fonts.rel = "stylesheet";
		fonts.href = FONT_HREF;
		document.head.appendChild(fonts);

		const icons = document.createElement("link");
		icons.rel = "stylesheet";
		icons.href = ICONS_HREF;
		document.head.appendChild(icons);

		return () => {
			document.head.removeChild(fonts);
			document.head.removeChild(icons);
		};
	}, []);

	const allFindings = TOPICS.flatMap((t) => t.findings);
	const filteredFindings =
		activeFilter === "all"
			? allFindings
			: allFindings.filter((f) => f.type === activeFilter);

	return (
		<div className="bg-warm-gray text-ivory/90 selection:bg-gold selection:text-warm-gray relative min-h-screen font-sans antialiased">
			<LandingHeader />

			<main className="relative pt-28 pb-32">
				{/* Compact Hero */}
				<section className="mx-auto max-w-[960px] px-6 lg:px-12">
					<div className="flex gap-8">
						{/* Thumbnail */}
						<div className="border-ivory/10 relative aspect-video w-[340px] shrink-0 overflow-hidden rounded-lg border bg-black/40">
							<img
								src={CONTENT.thumbnailUrl}
								alt=""
								className="h-full w-full object-cover opacity-60"
							/>
							{/* Play button */}
							<div className="absolute inset-0 flex items-center justify-center">
								<div className="text-ivory/50 flex h-14 w-14 items-center justify-center">
									<span className="material-symbols-outlined text-4xl">
										play_arrow
									</span>
								</div>
							</div>
						</div>

						{/* Info */}
						<div className="flex min-w-0 flex-col justify-center">
							<span className="text-accent mb-2 text-xs font-medium tracking-wider uppercase">
								{CONTENT.sourceType}
							</span>
							<h1 className="font-display text-ivory mb-3 text-3xl leading-snug tracking-tight">
								{CONTENT.title}
							</h1>
							<p className="text-ivory/50 mb-3 text-sm">
								{CONTENT.source}{" "}
								<span className="text-ivory/30 mx-1">
									&bull;
								</span>{" "}
								{CONTENT.duration}
							</p>
							<p className="text-ivory/35 text-sm leading-relaxed">
								{CONTENT.description}
							</p>
						</div>
					</div>
				</section>

				{/* Filter bar */}
				<section className="mx-auto mt-12 max-w-[960px] px-6 lg:px-12">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-4">
							<span className="text-gold text-xs font-medium tracking-wider uppercase">
								{TOTAL_FINDINGS} Findings
							</span>
							<div className="bg-ivory/10 h-px w-12" />
						</div>
						<div className="flex items-center gap-1">
							{FILTER_OPTIONS.map((opt) => (
								<button
									key={opt.key}
									type="button"
									onClick={() => setActiveFilter(opt.key)}
									className={`px-4 py-1.5 text-sm transition-colors ${
										activeFilter === opt.key
											? "border-ivory/15 text-ivory rounded border"
											: "text-ivory/40 hover:text-ivory/60"
									}`}
								>
									{opt.label}
								</button>
							))}
						</div>
					</div>
				</section>

				{/* Findings list */}
				<div className="mx-auto mt-8 max-w-[960px] space-y-7 px-6 lg:px-12">
					{filteredFindings.map((finding) => (
						<FindingCard key={finding.id} finding={finding} />
					))}
				</div>
			</main>

			<LandingFooter />
		</div>
	);
}
